import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as THREE from '@phdsoft/three';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { GeometricElement } from 'src/app/platform/busines-data/GeometricSurfaceElement';
import { ReferenceDateEventsService } from 'src/app/template/view-mode-tools/view-mode-anomaly-labels/reference-date-events.service';
import { GraphicsMaterials } from '../graphics/graphics-materials';
import { createMaskHandlesForLightSection, MaskHandle } from '../graphics/lightsection-mask';
import {
  LightSectionMod,
  LightSectionModService,
  isAddAction,
  isHideAction,
} from './LightSectionModService';

const OVERLAY_GROUP_NAME = 'LightSectionMods';

// Overlay-map key prefix for custom-material overlays, so they never collide
// with repair-mod overlays keyed by the bare `${regionId}|${elementId}`.
const CUSTOM_KEY_PREFIX = 'custom|';

// Composite key helpers. `_EL_IDX` is per-mesh local AND a single elementId
// may appear in multiple regions' GLBs when a section is shared across regions
// (e.g. an "Upper Deck" referenced by both Region 2 and Region 3). Keying by
// `${regionId}|${...}` keeps cross-region elements from colliding.
function regionElementKey(regionId: string, elementId: string): string {
  return `${regionId}|${elementId}`;
}

function regionSectionKey(regionId: string, sectionId: string): string {
  return `${regionId}|${sectionId}`;
}

@Injectable({ providedIn: 'root' })
export class LightSectionModRenderer {
  private readonly handlesBySection: Map<string, MaskHandle[]> = new Map();
  private readonly handlesByElement: Map<string, MaskHandle[]> = new Map();
  private readonly overlays: Map<string, THREE.Mesh> = new Map();
  /** Custom-material elements (keyed `${regionId}|${elementId}`) with the section
   *  group each overlay is parented under (null → asset-level fallback group). */
  private readonly customElements: Map<string, { element: GeometricElement; parent: THREE.Object3D | null }> = new Map();
  private overlayGroup: THREE.Group | null = null;
  private detach$ = new Subject<void>();
  private attached = false;
  private currentAssetId: string | null = null;

  constructor(
    private readonly modService: LightSectionModService,
    private readonly refDateService: ReferenceDateEventsService,
    private readonly assetManager: AssetManager,
  ) {}

  /**
   * Register the merged lightsection meshes for a (region, section) pair. Safe
   * to call multiple times; duplicates are de-duped by mesh reference. Called
   * by AssetLoader after Section.setLightSectionMaterial() has run.
   *
   * regionId is required so that masks/lookups stay scoped per region — two
   * regions can include the same physical section with shared elementIds.
   */
  public registerLightSectionMeshes(regionId: string, sectionId: string, lightSectionGroup: THREE.Object3D | null | undefined): void {
    if (!lightSectionGroup || !regionId) return;
    const handles = createMaskHandlesForLightSection(lightSectionGroup);
    if (handles.length === 0) return;

    const sectionKey = regionSectionKey(regionId, sectionId);
    const existing = this.handlesBySection.get(sectionKey) ?? [];
    const seen = new Set(existing.map(h => h.mesh));
    for (const h of handles) {
      if (!seen.has(h.mesh)) existing.push(h);
    }
    this.handlesBySection.set(sectionKey, existing);

    for (const handle of handles) {
      for (const entry of handle.elementMap) {
        if (!entry?.elementId) continue;
        const key = regionElementKey(regionId, entry.elementId);
        const arr = this.handlesByElement.get(key) ?? [];
        if (!arr.includes(handle)) arr.push(handle);
        this.handlesByElement.set(key, arr);
      }
    }
  }

  /**
   * Register elements that carry a custom material (Visual.CustomMaterial recreated
   * by AssetLoader). In REGIONS mode their `_EL_IDX` is masked out of the merged
   * lightsection meshes and an overlay with the custom material is rendered instead
   * — same mechanism as repairs. Called by AssetLoader.newLoadScene per section.
   * `parent` is the section's group; overlays are parented under it so they are
   * shown and hidden with the section. Falls back to the asset-level overlay group
   * when omitted.
   */
  public registerCustomMaterialElements(regionId: string, sectionId: string, elements: GeometricElement[], parent: THREE.Object3D | null = null): void {
    if (!regionId || !elements?.length) return;
    for (const element of elements) {
      if (!element?.Guid || !element.customMaterial) continue;
      this.customElements.set(regionElementKey(regionId, element.Guid), { element, parent });
    }
    // Re-apply immediately when a section streams in while REGIONS mode is active
    if (this.attached && this.customElements.size > 0) {
      const date = this.assetManager.referenceDate ?? new Date();
      this.applyForDate(date);
    }
  }

  /**
   * Activate masking + overlays for the given asset. Subscribes to reference-date
   * changes and re-applies when mod data changes. Call when entering REGIONS mode.
   * Does not fetch on its own — mod preload is triggered from `loadRegionRepairs`
   * so users see repairs before overlays.
   */
  public async attach(assetId: string, parentGroup: THREE.Group): Promise<void> {
    if (this.attached && this.currentAssetId === assetId) return;
    if (this.attached) this.detach();

    this.currentAssetId = assetId;
    this.overlayGroup = new THREE.Group();
    this.overlayGroup.name = OVERLAY_GROUP_NAME;
    parentGroup.add(this.overlayGroup);

    this.refDateService.referenceDateChange$
      .pipe(takeUntil(this.detach$))
      .subscribe(event => {
        const date = event?.newDate ?? this.assetManager.referenceDate ?? new Date();
        this.applyForDate(date);
      });

    this.modService.modsChanged$
      .pipe(takeUntil(this.detach$))
      .subscribe(() => {
        // Wipe overlays before rebuilding so stale modIds from the previous fetch
        // can't survive a refresh. ModIds are not always stable across regenerations
        // (backend may issue a new GUID per build), so we can't rely on diff.
        this.disposeAllOverlays();
        const date = this.assetManager.referenceDate ?? new Date();
        this.applyForDate(date);
      });

    this.attached = true;

    // Custom-material masks/overlays don't depend on mod data — apply them even
    // when the mod service never loads.
    if (this.modService.isLoaded() || this.customElements.size > 0) {
      const initialDate = this.assetManager.referenceDate ?? new Date();
      this.applyForDate(initialDate);
    }
  }

  private disposeAllOverlays(): void {
    if (!this.overlayGroup) return;
    for (const mesh of this.overlays.values()) {
      mesh.removeFromParent();
      // Custom-material overlays share the SECTIONS-mode element geometry — never dispose it
      if (!mesh.userData.sharedGeometry) mesh.geometry?.dispose();
    }
    this.overlays.clear();
  }

  /**
   * Deactivate. Clears all masks, removes overlays. Patched materials stay bound
   * to the meshes until reset(). Call when leaving REGIONS mode.
   */
  public detach(): void {
    if (!this.attached) return;
    this.detach$.next();
    this.detach$ = new Subject<void>();

    for (const handles of this.handlesBySection.values()) {
      for (const handle of handles) handle.clear();
    }

    if (this.overlayGroup) {
      for (const mesh of this.overlays.values()) {
        mesh.removeFromParent();
        // Custom-material overlays share the SECTIONS-mode element geometry — never dispose it
        if (!mesh.userData.sharedGeometry) mesh.geometry?.dispose();
      }
      this.overlayGroup.parent?.remove(this.overlayGroup);
    }
    this.overlays.clear();
    this.overlayGroup = null;
    this.attached = false;
  }

  /**
   * Release everything (mask handles, materials, indices, mod cache). Use when
   * unloading the asset or switching assets.
   */
  public reset(): void {
    this.detach();
    for (const handles of this.handlesBySection.values()) {
      for (const handle of handles) handle.dispose();
    }
    this.handlesBySection.clear();
    this.handlesByElement.clear();
    this.customElements.clear();
    this.currentAssetId = null;
    this.modService.invalidate();
  }

  private applyForDate(referenceDate: Date): void {
    if (!this.attached || !this.overlayGroup) return;
    // Custom-material masks/overlays are load-time data and don't need mods —
    // proceed without them when the mod service hasn't loaded (yet).
    const modsLoaded = this.modService.isLoaded();
    if (!modsLoaded && this.customElements.size === 0) return;

    // activeMods is keyed by `${regionId}|${elementId}` (see LightSectionModService.getActiveModsAt)
    const activeMods = modsLoaded ? this.modService.getActiveModsAt(referenceDate) : new Map<string, LightSectionMod>();

    // Elements masked/replaced by an active repair mod. A mod on an element takes
    // precedence over its custom material (the PostGeometry replaces the plate).
    const maskingModKeys = new Set<string>();
    for (const [key, mod] of activeMods) {
      if (!isAddAction(mod.Action)) maskingModKeys.add(key); // doublers don't mask/replace
    }

    // Step 1: update masks per handle — union of repair mods and custom-material elements.
    const handleToMasked = new Map<MaskHandle, number[]>();
    const addMaskFor = (regionId: string, elementId: string): void => {
      const handles = this.handlesByElement.get(regionElementKey(regionId, elementId));
      if (!handles) return;
      for (const handle of handles) {
        const idx = handle.indexOfElement(elementId);
        if (idx < 0) continue;
        const arr = handleToMasked.get(handle) ?? [];
        arr.push(idx);
        handleToMasked.set(handle, arr);
      }
    };

    for (const mod of activeMods.values()) {
      if (isAddAction(mod.Action)) continue; // doublers don't mask
      if (!mod.RegionId || !mod.ElementId) continue;
      addMaskFor(mod.RegionId, mod.ElementId);
    }
    for (const [key, entry] of this.customElements) {
      if (maskingModKeys.has(key)) continue; // repair replaces the element — its mask/overlay wins
      const regionId = key.slice(0, key.indexOf('|'));
      addMaskFor(regionId, entry.element.Guid);
    }

    // Apply masks: every handle either gets its computed list or is cleared.
    for (const handles of this.handlesBySection.values()) {
      for (const handle of handles) {
        const masked = handleToMasked.get(handle);
        if (masked && masked.length > 0) handle.setMasked(masked);
        else handle.clear();
      }
    }

    // Step 2: diff overlays. Each overlay is parented under its section group so it
    // is shown/hidden with the section (asset-level group only as a fallback);
    // identity stays (regionId, elementId).
    const desired = new Map<string, { mod: LightSectionMod; parent: THREE.Object3D }>();
    for (const mod of activeMods.values()) {
      if (isHideAction(mod.Action)) continue; // hide-only: no overlay
      if (!mod.PostGeometry?.V?.length) continue;
      const parent = this.overlayParentFor(mod.RegionId, mod.SectionId) ?? this.overlayGroup;
      desired.set(regionElementKey(mod.RegionId, mod.ElementId), { mod, parent });
    }

    // Custom-material overlays, keyed with a prefix so they never collide with mod overlays.
    const desiredCustom = new Map<string, { element: GeometricElement; parent: THREE.Object3D }>();
    for (const [key, entry] of this.customElements) {
      if (maskingModKeys.has(key)) continue; // repair replaces the element
      desiredCustom.set(CUSTOM_KEY_PREFIX + key, { element: entry.element, parent: entry.parent ?? this.overlayGroup });
    }

    // An overlay whose parent is no longer the desired one (e.g. the section group was
    // replaced when the region reloaded) is dropped here and rebuilt below.
    for (const [key, mesh] of Array.from(this.overlays.entries())) {
      const target = key.startsWith(CUSTOM_KEY_PREFIX) ? desiredCustom.get(key) : desired.get(key);
      const keep = !!target && mesh.parent === target.parent;
      if (!keep) {
        mesh.removeFromParent();
        // Custom-material overlays share the SECTIONS-mode element geometry — never dispose it
        if (!mesh.userData.sharedGeometry) mesh.geometry?.dispose();
        this.overlays.delete(key);
      }
    }
    for (const [key, entry] of desired) {
      if (this.overlays.has(key)) continue;
      const mesh = this.buildOverlayMesh(entry.mod, entry.parent);
      if (mesh) this.overlays.set(key, mesh);
    }
    for (const [key, entry] of desiredCustom) {
      if (this.overlays.has(key)) continue;
      const mesh = this.buildCustomOverlayMesh(entry.element, entry.parent);
      if (mesh) this.overlays.set(key, mesh);
    }
  }

  /** Section group that owns a (regionId, sectionId) pair, or null when the asset
   *  does not expose it. Overlays parented here follow the section's visibility. */
  private overlayParentFor(regionId: string, sectionId: string): THREE.Object3D | null {
    const regions = this.assetManager?.asset?.GetAllStructureRegions?.() ?? [];
    const region = regions.find(r => r?.Guid === regionId);
    const section = region?.sections?.find(s => s?.Guid === sectionId);
    return section?.group ?? region?.group ?? null;
  }

  /** Parent `mesh` under `parent`, expressing `sourceWorld` (the world matrix the
   *  overlay geometry is authored in) in the parent's frame. */
  private placeOverlay(mesh: THREE.Mesh, parent: THREE.Object3D, sourceWorld: THREE.Matrix4): void {
    parent.updateWorldMatrix(true, false);
    mesh.matrixAutoUpdate = false;
    mesh.matrix.copy(parent.matrixWorld).invert().multiply(sourceWorld);
    parent.add(mesh);
  }

  /**
   * Overlay for an element with a custom material: reuses the detailed element's
   * geometry (shared by reference — flagged so it is never disposed with the
   * overlay) and the custom material recreated by AssetLoader, placed at the
   * element's world transform expressed in `parent`'s frame and parented under it.
   */
  private buildCustomOverlayMesh(element: GeometricElement, parent: THREE.Object3D): THREE.Mesh | null {
    const source = element?.SurfaceDesign;
    const material = element?.customMaterial;
    if (!source?.geometry || !material || !this.overlayGroup) return null;

    const mesh = new THREE.Mesh(source.geometry, material);
    mesh.name = `LightSectionCustomMaterial_${element.Guid}`;
    mesh.renderOrder = 2;
    mesh.userData.sharedGeometry = true;
    mesh.userData.elementId = element.Guid;
    mesh.userData.lightSectionOverlay = true; // REGIONS view modes recolour it (GraphicsMaterials)

    source.updateWorldMatrix(true, false);
    this.placeOverlay(mesh, parent, source.matrixWorld);
    return mesh;
  }

  private buildOverlayMesh(mod: LightSectionMod, parent: THREE.Object3D): THREE.Mesh | null {
    const post = mod.PostGeometry;
    if (!post?.V?.length || !this.overlayGroup) return null;

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(post.V), 3));
    if (post.F?.length) {
      const vertexCount = post.V.length / 3;
      const IndexCtor = vertexCount > 65535 ? Uint32Array : Uint16Array;
      geom.setIndex(new THREE.BufferAttribute(new IndexCtor(post.F), 1));
    }
    geom.computeVertexNormals();

    const material = isAddAction(mod.Action)
      ? GraphicsMaterials.doublerBasicMaterial ?? GraphicsMaterials.basicMaterial
      : GraphicsMaterials.basicMaterial;

    const mesh = new THREE.Mesh(geom, material);
    mesh.name = `LightSectionMod_${mod.RegionId}_${mod.ElementId}`;
    mesh.renderOrder = 2;
    mesh.userData.modId = mod.ModId;
    mesh.userData.elementId = mod.ElementId;
    mesh.userData.regionId = mod.RegionId;
    mesh.userData.action = mod.Action;
    mesh.userData.lightSectionOverlay = true; // REGIONS view modes recolour it (GraphicsMaterials)

    // PostGeometry is authored in the asset-level frame the overlay group lives in
    this.overlayGroup.updateWorldMatrix(true, false);
    this.placeOverlay(mesh, parent, this.overlayGroup.matrixWorld);
    return mesh;
  }
}
